import {
  flattenNestedGroups,
  getGroupsMatchingSearchTerm,
  isGroupMatchingSearchTerm,
} from '@disco/utils/groups'

import type {
  GroupId,
  GroupWithNesting,
  IndentedNestedGroup,
} from 'types/group'

export const getExpandedLookup = (
  groups: GroupWithNesting[],
  searchTerm: string,
): Record<string, boolean> => {
  const expanded: Record<string, boolean> = {}
  const term = searchTerm.toLowerCase()

  if (!term.length) {
    groups.forEach(g => (expanded[g.id] = false))
    return expanded
  }

  const expandedGroups = (group: GroupWithNesting): boolean => {
    const children = group?.folders ?? []

    let descendantHasMatch = false
    for (const child of children) {
      const childMatches = isGroupMatchingSearchTerm(child, term)
      const childSubtreeMatches = expandedGroups(child)
      if (childMatches || childSubtreeMatches) {
        descendantHasMatch = true
      }
    }

    expanded[group.id] = descendantHasMatch

    return isGroupMatchingSearchTerm(group, term) || descendantHasMatch
  }

  groups.forEach(expandedGroups)
  return expanded
}

export const getFilteredGroups = (
  groups: IndentedNestedGroup[],
  searchTerm: string,
): IndentedNestedGroup[] => {
  return groups
    .map(group => {
      if (group?.business_role === 'partner') return null // Exclude partner groups from the list
      const matches = isGroupMatchingSearchTerm(group, searchTerm)

      const filteredFolders = group.folders
        ? getFilteredGroups(group.folders, searchTerm)
        : []

      const subfolders =
        matches && group.folders ? group.folders : filteredFolders

      if (matches || subfolders.length > 0) {
        return {
          ...group,
          folders: subfolders.length ? subfolders : undefined,
        }
      }

      return null
    })
    .filter(Boolean) as IndentedNestedGroup[]
}

export const flattenGroupsWithIndent = (
  groups: IndentedNestedGroup[],
  searchTerm: string,
  visited = new Set<number>(),
  selectedIds?: Set<number>,
  expanded?: Record<string, boolean>,
): IndentedNestedGroup[] => {
  const filteredGroups = getFilteredGroups(groups, searchTerm)
  // const formattedGroups = flattenNestedGroups(filteredGroups)
  let matchingIds = selectedIds
  let expandedLookup = expanded

  const searchedGroups = getGroupsMatchingSearchTerm(filteredGroups, searchTerm)

  if (!matchingIds) {
    matchingIds = new Set(searchedGroups.map(g => g.id))
  }

  if (!expandedLookup) {
    expandedLookup = getExpandedLookup(searchedGroups, searchTerm)
  }

  const result: IndentedNestedGroup[] = []

  for (const group of filteredGroups) {
    if (visited.has(group.id)) continue
    visited.add(group.id)

    const isMatching = matchingIds.has(group.id)

    let folders: IndentedNestedGroup[] = []
    if (group?.folders?.length) {
      const filteredFolders = getFilteredFoldersAndSubfolders(
        group.folders,
        matchingIds,
        searchTerm,
      )

      if (filteredFolders?.length) {
        folders = filteredFolders.map(folder => ({
          ...folder,
          folders: folder.folders,
          expanded: expandedLookup[folder.id] || false,
        }))
      }
    }

    if (isMatching || folders.length > 0) {
      result.push({
        ...group,
        indent: 0,
        expanded: expandedLookup[group.id] || false,
        folders: folders || undefined,
      })
    }
  }

  return result
}

export const selectedIdsKeys = (g: {
  parent: string | number | null | undefined
  id: string | number
}) => `${g.parent}:${g.id}`

type SearchGroupSelectedType = {
  isMatchingSearchTerm: boolean
  isSelected: boolean
}
export const isSearchedGroupSelected = (
  selectedIds,
  searchTerm,
  group,
): SearchGroupSelectedType => {
  if ((!searchTerm.length && !selectedIds) || !group.name)
    return { isMatchingSearchTerm: false, isSelected: false }
  const isMatchingSearch = isGroupMatchingSearchTerm(group, searchTerm)

  return {
    isMatchingSearchTerm: isMatchingSearch,
    isSelected: selectedIds.has(
      selectedIdsKeys({ parent: group.parent, id: group.id }),
    ),
  }
}

export function getFilteredFoldersAndSubfolders(
  folders,
  selectedIds,
  searchTerm,
): IndentedNestedGroup[] | undefined {
  const formattedFolders = folders?.reduce((acc, f) => {
    let formattedFolder: IndentedNestedGroup = {
      ...f,
      indent: f.indent || 1,
    }
    const { isMatchingSearchTerm: folderMatching, isSelected: folderSelected } =
      isSearchedGroupSelected(selectedIds, searchTerm, f)

    if (f.folders?.length) {
      const subfolders = f.folders.reduce((subAcc, subfolder) => {
        const { isSelected: subfolderSelected } = isSearchedGroupSelected(
          selectedIds,
          searchTerm,
          subfolder,
        )

        if (!subfolderSelected) {
          subAcc.push({
            ...subfolder,
            indent: (formattedFolder.indent ?? 1) + 1,
          })
        }
        return subAcc
      }, [])

      formattedFolder = {
        ...f,
        folders: subfolders?.length ? subfolders : undefined,
      }
    }

    const shouldExcludeFolder =
      (folderSelected && !formattedFolder.folders?.length) ||
      (!folderMatching && folderSelected && !formattedFolder.folders?.length)

    if (!shouldExcludeFolder) {
      acc.push(formattedFolder)
    }

    return acc
  }, [])

  return formattedFolders
}

export const updateGroupsExpanded = (
  id: GroupId,
  initialGroup: IndentedNestedGroup[],
) => {
  return initialGroup.map(group => {
    const updatedFolders = group.folders?.map(folder => {
      if (folder.id === id) {
        return { ...folder, expanded: !folder.expanded }
      }
      return folder
    })

    if (group.id === id) {
      return {
        ...group,
        expanded: !group.expanded,
        folders: updatedFolders,
      }
    }
    return { ...group, folders: updatedFolders }
  })
}
